import React from 'react';
import { Snackbar, Alert, AlertTitle, Slide } from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

/**
 * Reusable Toast Notification component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the toast is open
 * @param {function} props.onClose - Function to close the toast
 * @param {string} props.message - Toast message
 * @param {string} props.title - Optional toast title
 * @param {string} [props.severity] - Toast severity ('success', 'error', 'warning', 'info') (default: "info")
 * @param {number} [props.autoHideDuration] - Auto hide duration in ms (default: 6000)
 * @param {string} [props.position] - Toast position (default: "top-right")
 */

function SlideTransition(props) {
  return <Slide {...props} direction='left' />;
}

const ToastNotification = ({
  open,
  onClose,
  message,
  title,
  severity = 'info',
  autoHideDuration = 6000,
  position = 'top-right',
}) => {
  const getAnchorOrigin = (position) => {
    switch (position) {
      case 'top-left':
        return { vertical: 'top', horizontal: 'left' };
      case 'top-center':
        return { vertical: 'top', horizontal: 'center' };
      case 'top-right':
        return { vertical: 'top', horizontal: 'right' };
      case 'bottom-left':
        return { vertical: 'bottom', horizontal: 'left' };
      case 'bottom-center':
        return { vertical: 'bottom', horizontal: 'center' };
      case 'bottom-right':
        return { vertical: 'bottom', horizontal: 'right' };
      default:
        return { vertical: 'top', horizontal: 'right' };
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <ErrorIcon />;
      case 'warning':
        return <WarningIcon />;
      case 'info':
        return <InfoIcon />;
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={getAnchorOrigin(position)}
      TransitionComponent={SlideTransition}
      sx={{
        '& .MuiSnackbarContent-root': {
          minWidth: '300px',
          maxWidth: '500px',
        },
      }}
    >
      <Alert
        onClose={onClose}
        severity={severity}
        variant='filled'
        icon={getSeverityIcon(severity)}
        sx={{
          width: '100%',
          borderRadius: 2,
          boxShadow: 3,
          '& .MuiAlert-message': {
            padding: title ? '8px 0' : '4px 0',
          },
        }}
      >
        {title && (
          <AlertTitle sx={{ marginBottom: 0.5, fontWeight: 600 }}>
            {title}
          </AlertTitle>
        )}
        {message}
      </Alert>
    </Snackbar>
  );
};

export default ToastNotification;
